import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency to BRL
export function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
}

// Format date to Brazilian format
export function formatDate(dateString) {
  if (!dateString) return '—';
  const date = new Date(String(dateString).slice(0, 10) + 'T00:00:00');
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
}

// Format datetime to Brazilian format
export function formatDateTime(dateTimeString) {
  if (!dateTimeString) return '';
  const date = new Date(dateTimeString);
  return date.toLocaleString('pt-BR');
}

// Get status badge class
export function getStatusClass(status) {
  const statusMap = {
    'Agendado': 'status-agendado',
    'Em andamento': 'status-em-andamento',
    'Concluído': 'status-concluido',
    'Cancelado': 'status-cancelado',
    'Pendente pagamento': 'status-pendente-pagamento'
  };
  return statusMap[status] || 'bg-gray-500 text-white';
}

// Get status color for UI
export function getStatusColor(status) {
  const colorMap = {
    'Agendado': '#3B82F6',
    'Em andamento': '#F59E0B',
    'Concluído': '#10B981',
    'Cancelado': '#EF4444',
    'Pendente pagamento': '#8B5CF6'
  };
  return colorMap[status] || '#6B7280';
}

// Format address to string
export function formatEndereco(endereco) {
  if (!endereco) return '';
  // If already a string (e.g. from Orçamento), return as-is
  if (typeof endereco === 'string') return endereco;
  const parts = [
    endereco.rua,
    endereco.numero,
    endereco.complemento,
    endereco.bairro,
    endereco.cidade,
    endereco.estado,
    endereco.cep
  ].filter(Boolean);
  return parts.join(', ');
}

export function formatOrdemNumero(numero) {
  if (numero === null || numero === undefined || numero === '') return '';
  return `#OS-${String(numero).padStart(4, '0')}`;
}

function qtdSuffix(it) {
  const q = Number(it.quantidade || 1);
  const qTxt = String(q).replace('.', ',');
  if (it.unidade === 'm2') return ` (${qTxt} m²)`;
  return q > 1 ? ` (${qTxt}x)` : '';
}

// Format a list of ordem itens as multi-line text
function formatItensList(itens) {
  if (!itens || itens.length === 0) return '';
  return itens.map((it, idx) => {
    const subtotal = Number(it.subtotal || 0) || (Number(it.valor_unitario || 0) * Number(it.quantidade || 1));
    return `  ${idx + 1}) ${it.servico_nome}${qtdSuffix(it)} — ${formatCurrency(subtotal)}`;
  }).join('\n');
}

// Lista de itens SEM valores (usada na mensagem do colaborador)
function formatItensListSemValor(itens) {
  if (!itens || itens.length === 0) return '';
  return itens.map((it, idx) => {
    return `  ${idx + 1}) ${it.servico_nome}${qtdSuffix(it)}`;
  }).join('\n');
}

// Generate WhatsApp message to check provider availability (sem endereço completo e sem valores dos serviços)
export function generateWhatsAppMessageConsultaPrestador(ordem) {
  const dataFormatada = formatDate(ordem.data_agendamento);
  let diaSemana = '';
  try {
    diaSemana = new Date(`${ordem.data_agendamento}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' });
  } catch (e) { /* ignore */ }
  const itens = ordem.itens && ordem.itens.length > 0
    ? ordem.itens
    : [{ servico_nome: ordem.servico_nome, quantidade: 1 }];
  const servicosLista = itens
    .map((it) => `• ${it.servico_nome}${qtdSuffix(it)}`)
    .join('\n');
  const bairro = ordem.cliente_endereco?.bairro || ordem.bairro_atendimento || 'a confirmar';
  const quando = [diaSemana, dataFormatada].filter(Boolean).join(', ')
    + (ordem.periodo_atendimento ? ` - ${ordem.periodo_atendimento}` : (ordem.hora_agendamento ? ` - ${ordem.hora_agendamento}` : ''));

  const vpConsulta = Number(ordem.valor_prestador || 0);
  const deslocConsulta = Number(ordem.deslocamento || 0);
  const valorLinha = `💵 *Você recebe:* ${vpConsulta > 0 ? formatCurrency(vpConsulta) : 'R$ ____'}${deslocConsulta > 0 ? ` + ${formatCurrency(deslocConsulta)} de deslocamento` : ''}`;

  return `Olá, tudo bem? Estamos com uma demanda para atender e quero verificar a sua disponibilidade.

🔧 *Serviços a serem realizados:*
${servicosLista}

📍 *Bairro:* ${bairro}

📅 *Período:* ${quando}
${ordem.observacoes ? `\n📝 *Observações:* ${ordem.observacoes}\n` : ''}
${valorLinha}

Aguardo seu retorno imediato!`;
}

// Generate WhatsApp message for client
export function generateWhatsAppMessageCliente(ordem) {
  const dataFormatada = formatDate(ordem.data_agendamento);
  const enderecoFormatado = formatEndereco(ordem.cliente_endereco);
  const numeroOS = formatOrdemNumero(ordem.numero);
  const periodoLinha = ordem.periodo_atendimento
    ? `🕒 *Período:* ${ordem.periodo_atendimento}`
    : `⏰ *Horário:* ${ordem.hora_agendamento}`;
  
  const itens = ordem.itens && ordem.itens.length > 0
    ? ordem.itens
    : [{ servico_nome: ordem.servico_nome, quantidade: 1, subtotal: ordem.valor }];
  const servicosBlock = itens.length > 1
    ? `🔧 *Serviços:*\n${formatItensList(itens)}`
    : `🔧 *Serviço:* ${itens[0].servico_nome}${qtdSuffix(itens[0])}`;
  
  return `*Ordem de Serviço - Assistência Residencial*
${numeroOS ? `\n🆔 *Ordem:* ${numeroOS}` : ''}
Olá ${ordem.cliente_nome}!

Confirmamos seu agendamento:

📅 *Data:* ${dataFormatada}
${periodoLinha}
${servicosBlock}
👷 *Prestador:* ${ordem.colaborador_nome}
📍 *Local:* ${enderecoFormatado}
${Number(ordem.deslocamento || 0) > 0 ? `🚗 *Deslocamento:* ${formatCurrency(Number(ordem.deslocamento))}\n` : ''}💰 *Total:* ${formatCurrency(ordem.valor)}
${ordem.forma_pagamento ? `💳 *Pagamento:* ${ordem.forma_pagamento}` : ''}
${ordem.pix_link ? `🔗 *Pagamento via PIX:* ${ordem.pix_link}` : ''}
${ordem.observacoes ? `📝 *Observações:* ${ordem.observacoes}` : ''}

⚠️ *Observações importantes:*
• Qualquer dúvida ou problema entre em contato com nosso suporte por este número.
• Garantia do serviço de 30 dias corridos.
• Pagamento direto para a Tenance no link informado após o término do serviço.
• Lembrando que não fornecemos materiais/peças para a realização do serviço.

Qualquer dúvida estamos à disposição!`;
}

// Generate WhatsApp message for collaborator
export function generateWhatsAppMessageColaborador(ordem) {
  const dataFormatada = formatDate(ordem.data_agendamento);
  const enderecoFormatado = formatEndereco(ordem.cliente_endereco);
  const numeroOS = formatOrdemNumero(ordem.numero);
  const periodoInfo = ordem.periodo_atendimento
    ? ` (${ordem.periodo_atendimento})`
    : '';
  
  const itens = ordem.itens && ordem.itens.length > 0
    ? ordem.itens
    : [{ servico_nome: ordem.servico_nome, quantidade: 1, subtotal: ordem.valor }];
  const servicosBlock = itens.length > 1
    ? `🔧 *Serviços:*\n${formatItensListSemValor(itens)}`
    : `🔧 *Serviço:* ${itens[0].servico_nome}${qtdSuffix(itens[0])}`;
  
  const valorReceberLinha = (ordem.valor_prestador !== undefined && ordem.valor_prestador !== null && Number(ordem.valor_prestador) > 0)
    ? `💵 *Você recebe:* ${formatCurrency(ordem.valor_prestador)}\n`
    : '';
  const deslocColabLinha = Number(ordem.deslocamento || 0) > 0
    ? `🚗 *Deslocamento:* ${formatCurrency(Number(ordem.deslocamento))}\n`
    : '';
  
  return `*Nova Ordem de Serviço*
${numeroOS ? `\n🆔 *Ordem:* ${numeroOS}` : ''}
Olá ${ordem.colaborador_nome}!

Você tem um novo serviço agendado:

📅 *Data:* ${dataFormatada}
⏰ *Horário:* ${ordem.hora_agendamento}${periodoInfo}
${servicosBlock}
👤 *Cliente:* ${ordem.cliente_nome}
📍 *Endereço:* ${enderecoFormatado}
${valorReceberLinha}${deslocColabLinha}${ordem.forma_pagamento ? `💳 *Pagamento:* ${ordem.forma_pagamento}` : ''}
${ordem.observacoes ? `📝 *Observações:* ${ordem.observacoes}` : ''}

Bom trabalho!`;
}

// Generate WhatsApp message with all OS of the day for a collaborator (Grade Operacional)
export function generateWhatsAppMessageGradeDia(colaboradorNome, ordensDoDia, dataStr) {
  if (!ordensDoDia || ordensDoDia.length === 0) return '';
  const dataFormatada = formatDate(dataStr);
  const linhas = ordensDoDia
    .slice()
    .sort((a, b) => (a.hora_agendamento || '').localeCompare(b.hora_agendamento || ''))
    .map((o, idx) => {
      const numeroOS = formatOrdemNumero(o.numero);
      const enderecoFormatado = formatEndereco(o.cliente_endereco);
      const periodoInfo = o.periodo_atendimento ? ` (${o.periodo_atendimento})` : '';
      const itens = o.itens && o.itens.length > 0 ? o.itens : null;
      let servicoTxt;
      if (itens && itens.length > 1) {
        servicoTxt = `\n   🔧 Serviços:\n${itens.map((it) => {
          const qtd = qtdSuffix(it);
          return `      • ${it.servico_nome}${qtd}`;
        }).join('\n')}`;
      } else if (itens && itens.length === 1) {
        const it = itens[0];
        const qtd = qtdSuffix(it);
        servicoTxt = ` - ${it.servico_nome}${qtd}`;
      } else {
        servicoTxt = ` - ${o.servico_nome}`;
      }
      const vp = (o.valor_prestador !== undefined && o.valor_prestador !== null && Number(o.valor_prestador) > 0)
        ? Number(o.valor_prestador)
        : null;
      return `${idx + 1}) ${o.hora_agendamento || '--:--'}${periodoInfo}${servicoTxt}${numeroOS ? ` — ${numeroOS}` : ''}
   👤 ${o.cliente_nome}${enderecoFormatado ? `\n   📍 ${enderecoFormatado}` : ''}${vp !== null ? `\n   💵 Você recebe: ${formatCurrency(vp)}` : ''}${o.observacoes ? `\n   📝 ${o.observacoes}` : ''}`;
    })
    .join('\n\n');
  
  const total = ordensDoDia.reduce((sum, o) => {
    const vp = (o.valor_prestador !== undefined && o.valor_prestador !== null) ? Number(o.valor_prestador) : 0;
    return sum + (isNaN(vp) ? 0 : vp);
  }, 0);
  return `*Sua agenda do dia ${dataFormatada} - Tenance*

Olá ${colaboradorNome}!

Você tem ${ordensDoDia.length} atendimento${ordensDoDia.length > 1 ? 's' : ''} hoje:

${linhas}

*Total do dia (você recebe):* ${formatCurrency(total)}

Bom trabalho!`;
}

// Estados brasileiros (UF → nome)
export const ESTADOS_BR = [
  { uf: 'AC', nome: 'Acre' }, { uf: 'AL', nome: 'Alagoas' }, { uf: 'AP', nome: 'Amapá' },
  { uf: 'AM', nome: 'Amazonas' }, { uf: 'BA', nome: 'Bahia' }, { uf: 'CE', nome: 'Ceará' },
  { uf: 'DF', nome: 'Distrito Federal' }, { uf: 'ES', nome: 'Espírito Santo' }, { uf: 'GO', nome: 'Goiás' },
  { uf: 'MA', nome: 'Maranhão' }, { uf: 'MT', nome: 'Mato Grosso' }, { uf: 'MS', nome: 'Mato Grosso do Sul' },
  { uf: 'MG', nome: 'Minas Gerais' }, { uf: 'PA', nome: 'Pará' }, { uf: 'PB', nome: 'Paraíba' },
  { uf: 'PR', nome: 'Paraná' }, { uf: 'PE', nome: 'Pernambuco' }, { uf: 'PI', nome: 'Piauí' },
  { uf: 'RJ', nome: 'Rio de Janeiro' }, { uf: 'RN', nome: 'Rio Grande do Norte' }, { uf: 'RS', nome: 'Rio Grande do Sul' },
  { uf: 'RO', nome: 'Rondônia' }, { uf: 'RR', nome: 'Roraima' }, { uf: 'SC', nome: 'Santa Catarina' },
  { uf: 'SP', nome: 'São Paulo' }, { uf: 'SE', nome: 'Sergipe' }, { uf: 'TO', nome: 'Tocantins' }
];

export function getEstadoNome(uf) {
  const e = ESTADOS_BR.find((x) => x.uf === String(uf || '').toUpperCase());
  return e ? e.nome : uf;
}

// Cidades de atendimento
export const CIDADES_ATENDIMENTO = [
  'Belo Horizonte',
  'Contagem',
  'Betim'
];

// Períodos de atendimento (mostrados ao cliente apenas como nome; detalhes para empresa)
export const PERIODOS_ATENDIMENTO = [
  { value: 'Manhã', label: 'Manhã', horario: '08:00 às 12:00' },
  { value: 'Tarde', label: 'Tarde', horario: '13:00 às 17:30' }
];

export function getPeriodoHorario(periodo) {
  const p = PERIODOS_ATENDIMENTO.find((x) => x.value === periodo);
  return p ? p.horario : '';
}

// Generate WhatsApp message for orçamento
export function generateWhatsAppMessageOrcamento(orcamento) {
  const validadeFormatada = formatDate(orcamento.validade);
  const itens = orcamento.itens && orcamento.itens.length > 0 ? orcamento.itens : null;
  let servicosBlock;
  if (itens && itens.length > 1) {
    servicosBlock = `🔧 *Serviços:*\n${itens.map((it, i) => {
      const qtd = qtdSuffix(it);
      const sub = Number(it.subtotal || 0) || Number(it.valor_unitario || 0) * Number(it.quantidade || 1);
      return `  ${i + 1}) ${it.servico_nome}${qtd} — ${formatCurrency(sub)}`;
    }).join('\n')}`;
  } else if (itens && itens.length === 1) {
    const it = itens[0];
    const qtd = qtdSuffix(it);
    servicosBlock = `🔧 *Serviço:* ${it.servico_nome}${qtd}`;
  } else {
    servicosBlock = `🔧 *Serviço:* ${orcamento.descricao_servico}`;
  }
  const pct = Number(orcamento.desconto_percentual || 0);
  const bruto = Number(orcamento.valor_bruto || orcamento.valor || 0);
  const desloc = Number(orcamento.deslocamento || 0);
  const totalFinal = Number(orcamento.valor || 0);
  const descontoValor = bruto - (totalFinal - desloc);
  const linhasTotal = [];
  if (pct > 0) {
    linhasTotal.push(`💰 *Valor dos serviços:* ${formatCurrency(bruto)}`);
    linhasTotal.push(`🏷️ *Desconto aplicado:* ${String(pct).replace('.', ',')}% (− ${formatCurrency(descontoValor)})`);
  }
  if (desloc > 0) linhasTotal.push(`🚗 *Deslocamento:* ${formatCurrency(desloc)}`);
  linhasTotal.push(`💰 *Total${pct > 0 ? ' com desconto' : ''}:* ${formatCurrency(totalFinal)}`);
  const totalBlock = linhasTotal.join('\n');
  return `*Orçamento - Tenance Assistência Residencial*

Olá ${orcamento.cliente_nome}!

Conforme conversamos, segue o orçamento solicitado:

${servicosBlock}
${totalBlock}
📅 *Validade:* ${validadeFormatada}
${orcamento.periodo_atendimento ? `🕒 *Período de atendimento:* ${orcamento.periodo_atendimento}\n` : ''}${orcamento.cidade_atendimento ? `🏙️ *Cidade:* ${orcamento.cidade_atendimento}\n` : ''}${orcamento.cliente_endereco ? `📍 *Endereço:* ${orcamento.cliente_endereco}\n` : ''}${orcamento.observacoes ? `📝 *Observações:* ${orcamento.observacoes}\n` : ''}
⚠️ *Observações importantes:*
• Qualquer dúvida ou problema entre em contato com nosso suporte por este número.
• Garantia do serviço de 30 dias corridos.
• Pagamento direto para a Tenance no link informado após o término do serviço.
• Lembrando que não fornecemos materiais/peças para a realização do serviço.

Aguardamos sua confirmação para agendarmos o atendimento.
Qualquer dúvida estamos à disposição!`;
}

// Status options for Orçamento
export const ORCAMENTO_STATUS_OPTIONS = [
  'Em andamento',
  'Aguardando resposta',
  'Aprovado',
  'Negado',
  'Expirado'
];

// Get orçamento status color
export function getOrcamentoStatusColor(status) {
  const colorMap = {
    'Em andamento': '#3B82F6',
    'Aguardando resposta': '#F59E0B',
    'Aprovado': '#10B981',
    'Negado': '#EF4444',
    'Expirado': '#6B7280'
  };
  return colorMap[status] || '#6B7280';
}

// Copy text to clipboard
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
}

// Status options
export const STATUS_OPTIONS = [
  'Agendado',
  'Em andamento',
  'Concluído',
  'Cancelado',
  'Pendente pagamento'
];

// Payment method options
export const FORMAS_PAGAMENTO = [
  'Dinheiro',
  'Pix',
  'Cartão de Crédito',
  'Cartão de Débito',
  'Transferência'
];
